import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'

import { BaseComponent, Grid } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

interface HcssBid {
    id: string
    name: string
    estimateCode: string
    createdDate: Date | string | null
    estimateTotalCost: number | null
    actualBidMarkup: number | null
    actualMarkupTakeoff: number | null
    addonBondTotal: number | null
    addonCost: number | null
    addonMarkup: number | null
    bidDate: Date | string | null
    bidTotal: number | null
    lastModified: Date | string | null
}

interface HcssBusinessUnit {
    id: string
    name: string
}

interface TestConnectionResponse {
    success: boolean
    message: string
    data: string
}

interface SyncDatabaseResponse {
    message: string
    estimateCount: number
    activityCount: number
}

interface HcssApiActivity {
    id: string
    estimateId: string
    estimateCode: string
    activityCode: string
    description: string
    lastModified: Date | string | null
    manHours: number | null
    directTotalCost: number | null
    laborCost: number | null
    subcontractCost: number | null
    permanentMaterialCost: number | null
    equipmentCost: number | null
    constructionMaterialCost: number | null
    /** HCSS UI "Non-Add" (Y/N/empty) — when set, cost excluded from bid total */
    nonAdditive: string | null
    saveNonAdditive: string | null
}

/** Same shape as Bids → Heavy Bid Data → HCSS Activities (import) for side-by-side comparison with the API. */
interface HcssApiActivityComparisonRow {
    id: string
    estimateCode: string
    estimateName: string | null
    activityCode: string
    activityDescription: string
    dateCreated: Date | string | null
    manHours: number | null
    directTotalCost: number | null
    laborCost: number | null
    subcontractCost: number | null
    permanentMaterialCost: number | null
    equipmentCost: number | null
    constructionMaterialCost: number | null
    nonAdditive: string | null
    saveNonAdditive: string | null
}

@Component({
    selector: 'app-hcss-api-test',
    templateUrl: './hcss-api-test.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridSelectionCalculationComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class HcssApiTestComponent extends BaseComponent {
    public grid: Grid<HcssBid>
    public businessUnitsGrid: Grid<HcssBusinessUnit>
    public activitiesGrid: Grid<HcssApiActivity>
    public estimateCompareGrid: Grid<HcssApiActivityComparisonRow>
    public isLoadingTestConnection = false
    public isLoadingBusinessUnits = false
    public isLoadingBids = false
    public isLoadingCodePattern = false
    public isLoadingActivities = false
    public isLoadingCompareEstimate = false
    public isLoadingSyncDatabase = false
    public errorMessage: string | null = null
    public bids: HcssBid[] = []
    public businessUnits: HcssBusinessUnit[] = []
    public activities: HcssApiActivity[] = []
    public compareEstimateActivities: HcssApiActivityComparisonRow[] = []
    public readonly compareEstimateCode = '26035'
    public testConnectionResult: string | null = null
    public codePattern: string = ''

    constructor() {
        super()
        this.grid = new Grid<HcssBid>(this.services).filterBy(item => [item.name, item.estimateCode])
        this.businessUnitsGrid = new Grid<HcssBusinessUnit>(this.services).filterBy(item => [item.name, item.id])
        this.activitiesGrid = new Grid<HcssApiActivity>(this.services).filterBy(item => [
            item.estimateCode,
            item.activityCode,
            item.description,
            item.nonAdditive,
            item.saveNonAdditive,
        ])
        this.estimateCompareGrid = new Grid<HcssApiActivityComparisonRow>(this.services, 'id', this)
            .wrapColumnHeaders()
            .isReadOnly(true)
            .filterBy(item => [
                item.estimateCode,
                item.estimateName,
                item.activityCode,
                item.activityDescription,
                item.nonAdditive,
                item.saveNonAdditive,
            ])
    }

    public testConnection() {
        this.isLoadingTestConnection = true
        this.errorMessage = null
        this.testConnectionResult = null

        this.cancelableRequest(
            '',
            true,
            this.services.data.get<TestConnectionResponse>('/api/HcssApi/TestConnection').subscribe({
                next: result => {
                    this.isLoadingTestConnection = false
                    this.testConnectionResult = result.data || JSON.stringify(result, null, 2)
                    this.initialLoadingComplete()
                    this.services.notification.success('Connection test successful!')
                },
                error: error => {
                    this.isLoadingTestConnection = false
                    const errorMsg = error.error?.error || error.message || 'An error occurred while testing the connection'
                    this.errorMessage = errorMsg
                    this.initialLoadingComplete()
                    this.services.notification.error(errorMsg)
                },
            }),
        )
    }

    public getBusinessUnits() {
        this.isLoadingBusinessUnits = true
        this.errorMessage = null
        this.businessUnits = []

        this.cancelableRequest(
            '',
            true,
            this.services.data.get<HcssBusinessUnit[]>('/api/HcssApi/GetBusinessUnits').subscribe({
                next: result => {
                    this.isLoadingBusinessUnits = false
                    this.businessUnits = result || []
                    this.businessUnitsGrid.items = this.businessUnits
                    this.initialLoadingComplete()
                    this.services.notification.success(`Retrieved ${this.businessUnits.length} business unit(s)`)
                },
                error: error => {
                    this.isLoadingBusinessUnits = false
                    const errorMsg = error.error?.error || error.message || 'An error occurred while retrieving business units'
                    this.errorMessage = errorMsg
                    this.initialLoadingComplete()
                    this.services.notification.error(errorMsg)
                },
            }),
        )
    }

    public getBidsFromPast12Months() {
        this.isLoadingBids = true
        this.errorMessage = null
        this.bids = []

        this.cancelableRequest(
            '',
            true,
            this.services.data.get<HcssBid[]>('/api/HcssApi/GetBidsFromPast12Months').subscribe({
                next: result => {
                    this.isLoadingBids = false
                    // Convert date strings to Date objects for proper grid formatting
                    this.bids = (result || []).map(bid => ({
                        ...bid,
                        createdDate: bid.createdDate ? new Date(bid.createdDate as string) : null,
                        bidDate: bid.bidDate ? new Date(bid.bidDate as string) : null,
                        lastModified: bid.lastModified ? new Date(bid.lastModified as string) : null,
                    }))
                    this.grid.items = this.bids
                    this.initialLoadingComplete()
                    this.services.notification.success(`Retrieved ${this.bids.length} bid(s)`)
                },
                error: error => {
                    this.isLoadingBids = false
                    const errorMsg = error.error?.error || error.message || 'An error occurred while retrieving bids'
                    this.errorMessage = errorMsg
                    this.initialLoadingComplete()
                    this.services.notification.error(errorMsg)
                },
            }),
        )
    }

    public getEstimatesByCodePattern() {
        if (!this.codePattern || this.codePattern.trim() === '') {
            this.services.notification.warning('Please enter a code pattern to search for')
            return
        }

        this.isLoadingCodePattern = true
        this.errorMessage = null
        this.bids = []

        this.cancelableRequest(
            '',
            true,
            this.services.data.get<HcssBid[]>(`/api/HcssApi/GetEstimatesByCodePattern?codePattern=${encodeURIComponent(this.codePattern.trim())}`).subscribe({
                next: result => {
                    this.isLoadingCodePattern = false
                    // Convert date strings to Date objects for proper grid formatting
                    this.bids = (result || []).map(bid => ({
                        ...bid,
                        createdDate: bid.createdDate ? new Date(bid.createdDate as string) : null,
                        bidDate: bid.bidDate ? new Date(bid.bidDate as string) : null,
                        lastModified: bid.lastModified ? new Date(bid.lastModified as string) : null,
                    }))
                    this.grid.items = this.bids
                    this.initialLoadingComplete()
                    this.services.notification.success(`Found ${this.bids.length} estimate(s) matching code pattern "${this.codePattern}"`)
                },
                error: error => {
                    this.isLoadingCodePattern = false
                    const errorMsg = error.error?.error || error.message || 'An error occurred while searching for estimates'
                    this.errorMessage = errorMsg
                    this.initialLoadingComplete()
                    this.services.notification.error(errorMsg)
                },
            }),
        )
    }

    public getActivities() {
        this.isLoadingActivities = true
        this.errorMessage = null
        this.activities = []

        this.cancelableRequest(
            '',
            true,
            this.services.data.get<HcssApiActivity[]>('/api/HcssApi/GetActivities').subscribe({
                next: result => {
                    this.isLoadingActivities = false
                    // Convert date strings to Date objects for proper grid formatting
                    this.activities = (result || []).map(activity => ({
                        ...activity,
                        lastModified: activity.lastModified ? new Date(activity.lastModified as string) : null,
                    }))
                    this.activitiesGrid.items = this.activities
                    this.initialLoadingComplete()
                    this.services.notification.success(`Retrieved ${this.activities.length} activity(ies)`)
                },
                error: error => {
                    this.isLoadingActivities = false
                    const errorMsg = error.error?.error || error.message || 'An error occurred while retrieving activities'
                    this.errorMessage = errorMsg
                    this.initialLoadingComplete()
                    this.services.notification.error(errorMsg)
                },
            }),
        )
    }

    public syncDatabase() {
        this.isLoadingSyncDatabase = true
        this.errorMessage = null

        this.cancelableRequest(
            '',
            true,
            this.services.data.post<SyncDatabaseResponse>('/api/HcssApi/SyncDatabase', {}).subscribe({
                next: result => {
                    this.isLoadingSyncDatabase = false
                    this.initialLoadingComplete()
                    this.services.notification.success(
                        `${result.message} ${result.estimateCount} estimate(s), ${result.activityCount} activit${
                            result.activityCount === 1 ? 'y' : 'ies'
                        } written.`,
                    )
                },
                error: error => {
                    this.isLoadingSyncDatabase = false
                    const errorMsg =
                        error.error?.error || error.message || 'An error occurred while syncing HCSS data to the database'
                    this.errorMessage = errorMsg
                    this.initialLoadingComplete()
                    this.services.notification.error(errorMsg)
                },
            }),
        )
    }

    public getActivitiesForCompareEstimate() {
        this.isLoadingCompareEstimate = true
        this.errorMessage = null
        this.compareEstimateActivities = []

        this.cancelableRequest(
            '',
            true,
            this.services.data
                .get<HcssApiActivityComparisonRow[]>(
                    `/api/HcssApi/GetActivitiesForEstimate?estimateCode=${encodeURIComponent(this.compareEstimateCode)}`,
                )
                .subscribe({
                    next: result => {
                        this.isLoadingCompareEstimate = false
                        this.compareEstimateActivities = (result || []).map(row => ({
                            ...row,
                            dateCreated: row.dateCreated ? new Date(row.dateCreated as string) : null,
                        }))
                        this.estimateCompareGrid.items = this.compareEstimateActivities
                        this.initialLoadingComplete()
                        this.services.notification.success(
                            `Retrieved ${this.compareEstimateActivities.length} activit${
                                this.compareEstimateActivities.length === 1 ? 'y' : 'ies'
                            } for estimate code prefix "${this.compareEstimateCode}" (API)`,
                        )
                    },
                    error: error => {
                        this.isLoadingCompareEstimate = false
                        const errorMsg =
                            error.error?.error || error.message || 'An error occurred while fetching activities for estimate'
                        this.errorMessage = errorMsg
                        this.initialLoadingComplete()
                        this.services.notification.error(errorMsg)
                    },
                }),
        )
    }

    public get isLoading() {
        return (
            this.isLoadingTestConnection ||
            this.isLoadingBusinessUnits ||
            this.isLoadingBids ||
            this.isLoadingCodePattern ||
            this.isLoadingActivities ||
            this.isLoadingCompareEstimate ||
            this.isLoadingSyncDatabase
        )
    }
}
